import { DirectionalLight, DirectionalLightHelper } from 'three';
import { OrbitControls, useHelper } from '@react-three/drei';

import React from 'react';

// Camera configuration type
export interface CameraConfig {
  height: number;
  distance: number;
  lateralOffset: number;
  minSmoothness: number;
  maxSmoothness: number;
  followThreshold: number;
}

// Light helper component
export const LightDebugHelper: React.FC<{
  light: React.MutableRefObject<DirectionalLight>;
}> = ({ light }) => {
  useHelper(light, DirectionalLightHelper, 3, 'yellow');
  return null;
};

// Camera controls UI component
export const CameraControlsUI: React.FC<{
  cameraConfig: CameraConfig;
  setCameraConfig: (config: CameraConfig) => void;
}> = ({ cameraConfig, setCameraConfig }) => {
  // Update a single camera parameter
  const updateConfig = (key: keyof CameraConfig, value: number) => {
    setCameraConfig({ ...cameraConfig, [key]: value });
  };

  const style: React.CSSProperties = {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '10px',
    borderRadius: '5px',
    width: '250px',
    zIndex: 1000,
  };

  return (
    <div style={style}>
      <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
        Camera Settings
      </h3>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Height: {cameraConfig.height}
        </label>
        <input
          type="range"
          min="5"
          max="50"
          step="1"
          value={cameraConfig.height}
          onChange={(e) => updateConfig('height', parseFloat(e.target.value))}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Distance: {cameraConfig.distance}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={cameraConfig.distance}
          onChange={(e) => updateConfig('distance', parseFloat(e.target.value))}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Lateral Offset: {cameraConfig.lateralOffset}
        </label>
        <input
          type="range"
          min="-15"
          max="15"
          step="0.5"
          value={cameraConfig.lateralOffset}
          onChange={(e) =>
            updateConfig('lateralOffset', parseFloat(e.target.value))
          }
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Min Smoothness: {cameraConfig.minSmoothness?.toFixed(2)}
        </label>
        <input
          type="range"
          min="0.01"
          max="0.5"
          step="0.01"
          value={cameraConfig.minSmoothness || 0.1}
          onChange={(e) =>
            updateConfig('minSmoothness', parseFloat(e.target.value))
          }
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Max Smoothness: {cameraConfig.maxSmoothness?.toFixed(2)}
        </label>
        <input
          type="range"
          min="0.1"
          max="0.8"
          step="0.01"
          value={cameraConfig.maxSmoothness || 0.4}
          onChange={(e) =>
            updateConfig('maxSmoothness', parseFloat(e.target.value))
          }
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{ display: 'block', marginBottom: '2px' }}>
          Follow Threshold: {cameraConfig.followThreshold}
        </label>
        <input
          type="range"
          min="5"
          max="100"
          step="5"
          value={cameraConfig.followThreshold || 30}
          onChange={(e) =>
            updateConfig('followThreshold', parseFloat(e.target.value))
          }
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};

// Free-orbit camera for debugging
export const OrbitDebugCamera: React.FC = () => {
  return <OrbitControls makeDefault />;
};

const ENVIRONMENT_DEBUG_MODE = false;
// Toggle button for debug mode
export const DebugToggle: React.FC<{
  isDebugMode: boolean;
  setIsDebugMode: (value: boolean) => void;
}> = ({ isDebugMode, setIsDebugMode }) => {
  if (!ENVIRONMENT_DEBUG_MODE) return null;

  const style: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: isDebugMode
      ? 'rgba(255, 50, 50, 0.8)'
      : 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '5px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    zIndex: 1000,
  };

  return (
    <button style={style} onClick={() => setIsDebugMode(!isDebugMode)}>
      {isDebugMode ? 'Exit Debug Mode' : 'Debug Mode'}
    </button>
  );
};
